<template>
  <!-- First-launch permission rationale (native, once) -->
  <div v-if="showIntro" class="ng-overlay">
    <div class="ng-card">
      <i class="bi bi-shield-check ng-ico"></i>
      <h3>{{ $t('مرحباً بك في {name}', { name: brand.name }) }}</h3>
      <p class="ng-sub">{{ $t('لتعمل كلّ الميزات، سيطلب التطبيق إذنين:') }}</p>
      <ul class="ng-list">
        <li><i class="bi bi-bell-fill"></i> <span><b>{{ $t('الإشعارات') }}</b> {{ $t('— لتصلك التنبيهات الفوريّة.') }}</span></li>
        <li><i class="bi bi-geo-alt-fill"></i> <span><b>{{ $t('الموقع') }}</b> {{ $t('— لتسجيل مكان البصمة.') }}</span></li>
      </ul>
      <button class="ng-btn" :disabled="busy" @click="acceptIntro">{{ busy ? '...' : $t('متابعة') }}</button>
    </div>
  </div>

  <!-- Face ID lock -->
  <div v-if="locked" class="ng-overlay ng-lock">
    <div class="ng-card">
      <i class="bi bi-lock-fill ng-ico"></i>
      <h3>{{ $t('التطبيق مقفل') }}</h3>
      <p class="ng-sub">{{ $t('افتح ببصمة الوجه أو الإصبع.') }}</p>
      <button class="ng-btn" :disabled="busy" @click="unlock"><i class="bi bi-fingerprint"></i> {{ $t('فتح') }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '../stores/auth'
import brand from '../brand.js'
import {
  isNative, firstLaunchPending, runFirstLaunchPermissions,
  biometricLockEnabled, authenticateBiometric,
} from '../composables/useNativeSetup'

const auth = useAuthStore()
const showIntro = ref(false)
const locked = ref(false)
const busy = ref(false)
let wasHidden = false

async function gate() {
  if (!isNative()) return
  if (auth.isAuthenticated && biometricLockEnabled()) {
    locked.value = true
    await unlock()
  }
  if (!locked.value && firstLaunchPending()) showIntro.value = true
}
async function acceptIntro() {
  busy.value = true
  showIntro.value = false
  try { await runFirstLaunchPermissions() } finally { busy.value = false }
}
async function unlock() {
  busy.value = true
  try { if (await authenticateBiometric()) locked.value = false } finally { busy.value = false }
}
// Re-lock when the app returns from the background.
function onVisibility() {
  if (document.visibilityState === 'hidden') { wasHidden = true; return }
  if (wasHidden && isNative() && auth.isAuthenticated && biometricLockEnabled()) {
    wasHidden = false
    locked.value = true
    unlock()
  }
}
onMounted(() => { gate(); document.addEventListener('visibilitychange', onVisibility) })
onBeforeUnmount(() => document.removeEventListener('visibilitychange', onVisibility))
</script>

<style scoped>
.ng-overlay {
  position: fixed; inset: 0; z-index: 3000;
  background: var(--color-nav, var(--color-primary));
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.ng-card {
  background: #fff; border-radius: 20px; padding: 28px 22px; max-width: 360px; width: 100%;
  text-align: center; box-shadow: 0 20px 60px rgba(0,0,0,.25);
}
.ng-ico { font-size: 46px; color: var(--color-primary); }
.ng-card h3 { margin: 12px 0 4px; font-size: 19px; }
.ng-sub { color: var(--color-muted, #64748b); font-size: 14px; margin: 0 0 16px; }
.ng-list { list-style: none; padding: 0; margin: 0 0 20px; text-align: start; }
.ng-list li { display: flex; align-items: center; gap: 10px; padding: 8px 0; font-size: 14px; }
.ng-list li i { color: var(--color-primary); font-size: 18px; }
.ng-btn {
  width: 100%; padding: 14px; border: 0; border-radius: 12px; cursor: pointer;
  background: var(--color-primary); color: #fff; font-size: 16px; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.ng-btn:disabled { opacity: .6; }
</style>
